import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { PortletComponent } from '../../../../../shared/components/portlet/portlet/portlet.component';
import { CommonGridComponent } from '../../../../../shared/components/kendo/grid/components/common-grid/common-grid.component';
import {
  createTemplateNotificationGridConfig,
  getTemplateActions,
  TEMPLATE_NOTIFICATION_PORTLET_CONFIG,
} from '../../configurations/template-notification-grid.config';
import { FilterFieldConfig } from '../../../../../shared/components/kendo/grid/components/grid-filter/grid-filter.component';
import { TEMPLATE_FILTER_FIELDS } from '../../../../human-resources/template/configurations/template-grid.config';
import { FolderData } from '../../../../human-resources/template/models/template.model';
import {
  TemplateGridState,
  TemplatNotificationData,
} from '../../models/template-notification-model';
import { BaseGridComponent } from '../../../../../core/components/base-grid/base-grid.component';
import { TEMPLATE_CONSTANTS } from '../../../../human-resources/template/configurations/template-constants';
import { finalize, take } from 'rxjs';
import { TemplateNotificationService } from '../../services/template-notification.service';
import { OverlayscrollbarsModule } from 'overlayscrollbars-ngx';
import {
  checkMessageTypeAndMessage,
  DropMessageType,
} from '../../../../../shared/constant/validation-messages';

@Component({
  selector: 'app-template-notification-list',
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PortletComponent,
    CommonGridComponent,
    OverlayscrollbarsModule,
  ],
  templateUrl: './template-notification-list.component.html',
  styleUrl: './template-notification-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateNotificationListComponent extends BaseGridComponent<
  TemplatNotificationData,
  TemplateGridState
> {
  protected override handleAdd(): void {
    throw new Error('Method not implemented.');
  }
  private readonly templateNotificationService = inject(TemplateNotificationService);
  private readonly route = inject(Router);

  override readonly gridId = 'template-grid';
  override readonly apiEndpoint = 'api/Templates/GeFilesData';

  readonly portletConfig = TEMPLATE_NOTIFICATION_PORTLET_CONFIG;
  selectedFilter: any = '';

  filterFields: FilterFieldConfig[] = TEMPLATE_FILTER_FIELDS;
  filterConfig = {
    showLabels: false,
    showClearButton: true,
    showClearButtonText: true,
    layout: 'horizontal' as const,
    initialValues: {},
    debounceTime: 500,
  };

  folderData: FolderData[] = [];

  currentFolderId: number = 98;

  @Input() isNotificationView = false;

  protected override getDefaultPageSize(): number {
    return TEMPLATE_CONSTANTS.DEFAULT_PAGE_SIZE;
  }

  protected override getExtraInitialState(): Partial<TemplateGridState> {
    return { search: '', filterTypes: '' };
  }

  protected override setupPortletButtons(): void {
    this.portletButtons = [];
  }

  protected override setupGridConfig(): void {
    const actions = getTemplateActions({
      onFinishSigning: (item) => this.handleFinishSigning(item),
    });

    this.gridConfig = createTemplateNotificationGridConfig(actions);
  }

  protected override loadData(): void {
    this.loadTemplateDataList();
  }

  loadTemplateDataList() {
    this.loading$.next(true);
    this.cdr.markForCheck();

    this.templateNotificationService
      .getTemplateNotificationData(this.gridState)
      .pipe(
        take(1),
        finalize(() => {
          this.loading$.next(false);
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (result) => {
          if (result) {
            const { messageType, message } = checkMessageTypeAndMessage(result);
            if (messageType === DropMessageType.Success) {
              this.gridData = {
                data: result?.data?.lstdata,
                total: result?.data?.datacount ?? 0,
              };
            } else {
              this.gridData = { data: [], total: 0 };
              this.notificationService.error(message);
            }
            this.cdr.markForCheck();
          }
        },
        error: (error) => {
          console.error('Failed to load doctors:', error);
          this.gridData = { data: [], total: 0 };
          this.notificationService.error('Failed to load doctors');
          this.cdr.markForCheck();
        },
      });
  }

  handleFinishSigning(data: TemplatNotificationData) {
    this.route.navigate(['/notifications/template-notification/template-fill', data.id, data.fileId]);
  }

  handleFinishFromlistSigning(id: number, fileId: number) {
    this.route.navigate(['/notifications/template-notification/template-fill', id, fileId]);
  }

  handleCardClick(event: MouseEvent): void {
    event.stopPropagation();
  }
}
